import { Box, Button, Callout, Checkbox, Heading, Intent, Label, Select } from "@blasterjs/core";
import React, { useState } from "react";

import { useAppDispatch, useAppSelector } from "../hooks";
import { imageQualityAssessmentSave } from "../slices/caseImageViewer";
import {
  DeficientFeatureOption,
  ImageQualityAssessment,
  ImageQualityRating,
  UUID,
  formatImageQualityRating
} from "../models";
import { decodeImageQualityRatingString } from "../decoders";

interface Props {
  // The case the image belongs to (threaded to the save so it does not depend on store load order).
  readonly caseId: UUID;
  // The image the assessment applies to. The parent keys this component by imageId (and the loaded
  // resource) so switching images or saving remounts it with fresh initial state.
  readonly imageId: UUID;
  // The reader's previously-saved IQA for this image, if any (used to pre-fill).
  readonly savedAssessment: ImageQualityAssessment | null;
  readonly deficientFeatureOptions: ReadonlyArray<DeficientFeatureOption>;
  // Whether this image is H&E-stained. The "No evaluable Vh:Cd units" question is only shown for
  // H&E images (mirrors the backend ImageGrouping.isHeStain predicate).
  readonly isHeStain: boolean;
}

// "Other" is offered as an extra selectable feature backed by a free-text value; the backend keeps
// it in a separate deficientFeaturesOther field.
const OTHER = "Other";

const ImageQualityAssessmentForm = ({
  caseId,
  imageId,
  savedAssessment,
  deficientFeatureOptions,
  isHeStain
}: Props) => {
  const dispatch = useAppDispatch();

  // Live save state, so the button reflects an in-flight save / error while the form stays seeded
  // from the loaded resource.
  const isPending = useAppSelector(state => state.caseImageViewer.imageQualityAssessmentSaving);
  const errorMessage = useAppSelector(
    state => state.caseImageViewer.imageQualityAssessmentSaveError
  );

  const [imageEvaluable, setImageEvaluable] = useState<boolean>(
    savedAssessment ? savedAssessment.imageEvaluable : true
  );
  const [qualityAssessment, setQualityAssessment] = useState<ImageQualityRating | null>(
    savedAssessment ? savedAssessment.qualityAssessment : null
  );
  const [deficientFeatures, setDeficientFeatures] = useState<ReadonlyArray<string>>(
    savedAssessment ? savedAssessment.deficientFeatures : []
  );
  const [otherSelected, setOtherSelected] = useState<boolean>(
    savedAssessment ? savedAssessment.deficientFeaturesOther !== null : false
  );
  const [deficientFeaturesOther, setDeficientFeaturesOther] = useState<string>(
    savedAssessment && savedAssessment.deficientFeaturesOther
      ? savedAssessment.deficientFeaturesOther
      : ""
  );
  const [noEvaluableVhcdUnits, setNoEvaluableVhcdUnits] = useState<boolean>(
    savedAssessment ? savedAssessment.noEvaluableVhcdUnits : false
  );

  const onRatingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const result = decodeImageQualityRatingString(e.target.value);
    setQualityAssessment(result.isOk() ? result.value : null);
  };

  const toggleFeature = (label: string) =>
    setDeficientFeatures(
      deficientFeatures.includes(label)
        ? deficientFeatures.filter(f => f !== label)
        : [...deficientFeatures, label]
    );

  const onSave = () => {
    const trimmedOther = deficientFeaturesOther.trim();
    dispatch(
      imageQualityAssessmentSave({
        caseId,
        imageId,
        submission: {
          imageEvaluable,
          qualityAssessment,
          deficientFeatures,
          deficientFeaturesOther: otherSelected && trimmedOther !== "" ? trimmedOther : null,
          // Only meaningful for H&E images, where the question is shown; force false otherwise.
          noEvaluableVhcdUnits: isHeStain ? noEvaluableVhcdUnits : false
        }
      })
    );
  };

  return (
    <Box style={{ marginBottom: "0.6rem" }}>
      <Heading as="h4" style={{ marginBottom: "0.4rem" }}>
        Image Quality Assessment
      </Heading>

      <Box style={{ display: "flex", alignItems: "center", marginBottom: "0.4rem" }}>
        <Checkbox
          checked={imageEvaluable}
          onChange={() => setImageEvaluable(!imageEvaluable)}
          id="iqa-evaluable"
        />
        <Label htmlFor="iqa-evaluable" style={{ marginLeft: "0.4rem", marginBottom: 0 }}>
          Is the image evaluable?
        </Label>
      </Box>

      <Box style={{ marginBottom: "0.4rem" }}>
        <Label style={{ marginBottom: "0.2rem" }}>Quality Assessment</Label>
        <Select
          value={qualityAssessment === null ? "" : qualityAssessment}
          onChange={onRatingChange}
        >
          <option key="" value="" />
          {[ImageQualityRating.Poor, ImageQualityRating.Fair, ImageQualityRating.Good].map(
            rating => (
              <option key={rating} value={rating}>
                {formatImageQualityRating(rating)}
              </option>
            )
          )}
        </Select>
      </Box>

      <Box style={{ marginBottom: "0.4rem" }}>
        <Label style={{ marginBottom: "0.2rem" }}>Deficient Features</Label>
        {deficientFeatureOptions.map(option => (
          <Box
            key={option.id}
            style={{ display: "flex", alignItems: "center", marginBottom: "0.2rem" }}
          >
            <Checkbox
              checked={deficientFeatures.includes(option.label)}
              onChange={() => toggleFeature(option.label)}
              id={`iqa-feature-${option.id}`}
            />
            <Label
              htmlFor={`iqa-feature-${option.id}`}
              style={{ marginLeft: "0.4rem", marginBottom: 0 }}
            >
              {option.label}
            </Label>
          </Box>
        ))}
        <Box style={{ display: "flex", alignItems: "center", marginBottom: "0.2rem" }}>
          <Checkbox
            checked={otherSelected}
            onChange={() => setOtherSelected(!otherSelected)}
            id="iqa-feature-other"
          />
          <Label htmlFor="iqa-feature-other" style={{ marginLeft: "0.4rem", marginBottom: 0 }}>
            {OTHER}
          </Label>
        </Box>
        {otherSelected ? (
          <input
            type="text"
            value={deficientFeaturesOther}
            placeholder="Describe other deficient feature"
            onChange={e => setDeficientFeaturesOther(e.target.value)}
            style={{ width: "100%", marginTop: "0.2rem" }}
          />
        ) : null}
      </Box>

      {isHeStain ? (
        <Box style={{ display: "flex", alignItems: "center", marginBottom: "0.4rem" }}>
          <Checkbox
            checked={noEvaluableVhcdUnits}
            onChange={() => setNoEvaluableVhcdUnits(!noEvaluableVhcdUnits)}
            id="iqa-vhcd"
          />
          <Label htmlFor="iqa-vhcd" style={{ marginLeft: "0.4rem", marginBottom: 0 }}>
            No evaluable Vh:Cd units are present on this image
          </Label>
        </Box>
      ) : null}

      {errorMessage !== null ? (
        <Callout intent={Intent.DANGER} style={{ marginBottom: "0.4rem" }}>
          {errorMessage}
        </Callout>
      ) : null}

      <Button appearance="prominent" onClick={onSave} disabled={isPending}>
        {isPending ? "Saving…" : "Save Quality Assessment"}
      </Button>
    </Box>
  );
};

export default ImageQualityAssessmentForm;
